import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Alert,
  Anchor,
  Badge,
  Button,
  Group,
  Modal,
  Paper,
  Radio,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core'
import { can } from '@racedash/shared'
import { useShell } from '../AppShell'
import { useDashboards } from '../dashboards/useDashboards'
import type { Dashboard } from '../dashboards/useDashboards'
import {
  createDashboard,
  renameDashboard,
  setVisibility,
  softDeleteDashboard,
} from '../dashboards/api'
import { relativeTime } from '../lib/time'
import OrgNav from '../components/OrgNav'
import { screensForTemplate, templates } from '../dashboard/templates'
import type { TemplateId } from '../dashboard/templates'

export default function OrgDashboards() {
  const { user, currentOrg, role } = useShell()
  const { dashboards, error } = useDashboards(currentOrg?.id, user.uid)
  const [creating, setCreating] = useState(false)
  const [template, setTemplate] = useState<TemplateId>('standard')
  const [renaming, setRenaming] = useState<Dashboard | null>(null)

  if (!currentOrg) {
    return (
      <Paper withBorder p="lg" maw={560}>
        <Stack gap="xs">
          <Title order={1} size="h3">
            Not found
          </Title>
          <Text c="dimmed" size="sm">
            This organisation doesn't exist, or you aren't a member of it.{' '}
            <Anchor component={Link} to="/">
              Back to your dashboards
            </Anchor>
          </Text>
        </Stack>
      </Paper>
    )
  }

  const mayCreate = can(role, 'dashboard.create')
  const mayEdit = can(role, 'dashboard.edit')
  const mayDelete = can(role, 'dashboard.softDelete')
  const mayShare = can(role, 'dashboard.setVisibility')

  return (
    <Stack gap="lg">
      <Group justify="space-between" align="flex-start">
        <div>
          <Title order={1} size="h2">
            {currentOrg.kind === 'personal' ? 'Personal' : currentOrg.name}
          </Title>
          <Text c="dimmed" size="sm">
            You are {role === 'owner' ? 'an' : 'a'} <strong>{role}</strong> here
            {currentOrg.isPrimaryOwner && ' · primary owner'}
          </Text>
        </div>
        {mayCreate && <Button onClick={() => setCreating(true)}>New dashboard</Button>}
      </Group>

      <OrgNav orgId={currentOrg.id} role={role} />

      {error && <Alert color="red">{error}</Alert>}
      {!dashboards && !error && <Text c="dimmed">Loading…</Text>}

      {dashboards?.length === 0 && (
        <Paper withBorder p="xl">
          <Text c="dimmed" ta="center">
            No dashboards yet.
            {mayCreate
              ? ' Create one — everyone in this organisation will see it, and you decide separately whether to add a public link.'
              : ' Ask an editor or owner to create one.'}
          </Text>
        </Paper>
      )}

      {dashboards && dashboards.length > 0 && (
        <Paper withBorder>
          <Table.ScrollContainer minWidth={760}>
            <Table highlightOnHover verticalSpacing="xs" horizontalSpacing="sm">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Dashboard</Table.Th>
                  <Table.Th w={200}>Audience</Table.Th>
                  <Table.Th w={150}>Edited</Table.Th>
                  <Table.Th w={260} />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {dashboards.map((d) => {
                  const isPrivate = d.visibility === 'private'
                  return (
                    <Table.Tr key={d.id}>
                      <Table.Td>
                        <Anchor
                          component={Link}
                          to={`/d/${d.id}`}
                          fw={500}
                          c="inherit"
                          underline="hover"
                        >
                          {d.name}
                        </Anchor>
                        {d.createdBy !== user.uid && (
                          <Text size="xs" c="dimmed">
                            shared by a teammate
                          </Text>
                        )}
                      </Table.Td>
                      <Table.Td>
                        <Group gap="xs" wrap="nowrap">
                          {/* Always shown, so the audience is never ambiguous. */}
                          <Badge
                            variant="light"
                            size="sm"
                            color={isPrivate ? 'gray' : 'racedash'}
                            style={{ flex: 'none' }}
                          >
                            {isPrivate ? 'Only me' : 'Everyone'}
                          </Badge>
                          {/* Every member sees that a dashboard is publicly
                              linked, even viewers who may not read the token. */}
                          {d.hasActiveLink && (
                            <Badge
                              variant="light"
                              size="sm"
                              color="green"
                              style={{ flex: 'none' }}
                            >
                              Shared
                            </Badge>
                          )}
                        </Group>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" c="dimmed">
                          {relativeTime(d.updatedAt)}
                        </Text>
                      </Table.Td>
                      <Table.Td align="right">
                        <Group gap={4} justify="flex-end" wrap="nowrap">
                          {/* Two different things, deliberately labelled apart:
                              this governs who inside the organisation sees it.
                              The public link lives on the dashboard itself and
                              is a separate axis entirely. */}
                          {mayShare && (
                            <Button
                              variant="subtle"
                              size="compact-sm"
                              title={
                                isPrivate
                                  ? 'Let everyone in this organisation see it'
                                  : 'Hide it from the rest of the organisation. Any public link is suspended too.'
                              }
                              onClick={() =>
                                void setVisibility(d.id, isPrivate ? 'organisation' : 'private')
                              }
                            >
                              {isPrivate ? 'Share with org' : 'Make only me'}
                            </Button>
                          )}
                          {mayEdit && (
                            <Button
                              variant="subtle"
                              size="compact-sm"
                              onClick={() => setRenaming(d)}
                            >
                              Rename
                            </Button>
                          )}
                          {mayDelete && (
                            <Button
                              variant="subtle"
                              color="red"
                              size="compact-sm"
                              onClick={() => void softDeleteDashboard(d.id)}
                            >
                              Delete
                            </Button>
                          )}
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  )
                })}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </Paper>
      )}

      <NameDialog
        opened={creating}
        onClose={() => setCreating(false)}
        title="New dashboard"
        submitLabel="Create"
        onSubmit={(name) =>
          createDashboard(currentOrg.id, user.uid, name, screensForTemplate(template))
        }
      >
        {/* The layout is chosen here and written into the document, rather than
            filled in when the dashboard is opened. A dashboard that looked
            populated while its document was empty could not tell you whether it
            had unsaved changes. */}
        <Radio.Group
          label="Start from"
          value={template}
          onChange={(v) => setTemplate(v as TemplateId)}
        >
          <Stack gap="xs" mt="xs">
            {templates.map((t) => (
              <Radio key={t.id} value={t.id} label={t.label} description={t.description} />
            ))}
          </Stack>
        </Radio.Group>
      </NameDialog>
      <NameDialog
        opened={renaming !== null}
        onClose={() => setRenaming(null)}
        title={`Rename ${renaming?.name ?? ''}`}
        submitLabel="Save changes"
        initial={renaming?.name}
        onSubmit={(name) => renameDashboard(renaming!.id, name)}
      />
    </Stack>
  )
}

/**
 * Naming a dashboard, for both creating and renaming.
 *
 * One field, so one dialog serves both — and renaming through it rather than by
 * editing the row in place means the change is confirmed rather than committed
 * by a field losing focus, which is what the rest of the product does.
 */
function NameDialog({
  opened,
  onClose,
  title,
  submitLabel,
  initial,
  onSubmit,
  children,
}: {
  opened: boolean
  onClose: () => void
  title: string
  submitLabel: string
  initial?: string
  onSubmit: (name: string) => Promise<unknown>
  /** Anything the creating case needs and the renaming case does not. */
  children?: React.ReactNode
}) {
  const [name, setName] = useState('')
  const [baseline, setBaseline] = useState('')
  const [busy, setBusy] = useState(false)
  const [failed, setFailed] = useState<string | null>(null)

  useEffect(() => {
    if (!opened) return
    setName(initial ?? '')
    setBaseline(initial ?? '')
    setFailed(null)
  }, [opened, initial])

  const changed = name.trim() !== baseline.trim()
  const canSubmit = Boolean(name.trim()) && changed

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    if (busy || !canSubmit) return
    setBusy(true)
    setFailed(null)
    try {
      await onSubmit(name.trim())
      onClose()
    } catch (err) {
      setFailed(err instanceof Error ? err.message : 'Could not save.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal opened={opened} onClose={() => !busy && onClose()} title={title}>
      <form onSubmit={(e) => void submit(e)}>
        <Stack gap="md">
          {failed && <Alert color="red">{failed}</Alert>}
          <TextInput
            label="Name"
            value={name}
            onChange={(e) => setName(e.currentTarget.value)}
            placeholder="Race day"
            data-autofocus
            required
          />
          {children}
          <Group justify="flex-end">
            <Button variant="default" type="button" onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button type="submit" loading={busy} disabled={!canSubmit}>
              {submitLabel}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  )
}
